import {
  Invitation,
  Organisation,
  PlanRun,
  TaskPlan,
  Team,
  User,
} from "../types";

let API_BASE = import.meta.env.VITE_API_URL || "http://localhost:8787/api";
if (!API_BASE.endsWith("/api")) {
  API_BASE = `${API_BASE}/api`;
}

// Helper for API calls
async function apiFetch(
  path: string,
  options: RequestInit = {},
  token?: string,
) {
  if (token) {
    options.headers = {
      ...options.headers,
      Authorization: `Bearer ${token}`,
    };
  }

  const res = await fetch(`${API_BASE}${path}`, options);
  if (!res.ok) {
    const error = await res.json().catch(() => ({ error: "Unknown error" }));
    throw new Error(error.error || "API request failed");
  }
  return res.json();
}

// --- Organisations ---

export const getMyOrganisations = async (
  token?: string,
): Promise<Organisation[]> => {
  return apiFetch("/organisations", {}, token);
};

export const getDashboardData = async (
  orgId: string,
  token?: string,
): Promise<{ plans: TaskPlan[]; runs: PlanRun[] }> => {
  return apiFetch(`/organisations/${orgId}/dashboard`, {}, token);
};

export const saveOrganisation = async (
  org: Organisation & { planId?: string },
  token?: string,
): Promise<Organisation> => {
  return apiFetch(
    "/organisations",
    {
      method: "POST",
      body: JSON.stringify(org),
      headers: { "Content-Type": "application/json" },
    },
    token,
  );
};

export const renameOrganisation = async (
  id: string,
  name: string,
  token?: string,
): Promise<void> => {
  await apiFetch(
    `/organisations/${id}`,
    {
      method: "PATCH",
      body: JSON.stringify({ name }),
      headers: { "Content-Type": "application/json" },
    },
    token,
  );
};

export const updateGithubInstallation = async (
  id: string,
  githubInstallationId: string,
  token?: string,
): Promise<void> => {
  await apiFetch(
    `/organisations/${id}`,
    {
      method: "PATCH",
      body: JSON.stringify({ githubInstallationId }),
      headers: { "Content-Type": "application/json" },
    },
    token,
  );
};

export const getGithubRepositories = async (
  orgId: string,
  token?: string,
): Promise<{ id: number; name: string; full_name: string }[]> => {
  return apiFetch(`/organisations/${orgId}/github/repositories`, {}, token);
};

export const getGithubWorkflows = async (
  orgId: string,
  owner: string,
  repo: string,
  token?: string,
): Promise<{ id: number; name: string; path: string; state: string }[]> => {
  return apiFetch(
    `/organisations/${orgId}/github/repositories/${owner}/${repo}/workflows`,
    {},
    token,
  );
};

export const getGithubWorkflowInputs = async (
  orgId: string,
  owner: string,
  repo: string,
  path: string,
  token?: string,
): Promise<Record<string, any>> => {
  return apiFetch(
    `/organisations/${orgId}/github/repositories/${owner}/${repo}/workflows/inputs?path=${encodeURIComponent(path)}`,
    {},
    token,
  );
};

export const deleteOrganisation = async (
  id: string,
  token?: string,
): Promise<void> => {
  await apiFetch(
    `/organisations/${id}`,
    {
      method: "DELETE",
    },
    token,
  );
};

export const leaveOrganisation = async (
  id: string,
  token?: string,
): Promise<void> => {
  await apiFetch(
    `/organisations/${id}/leave`,
    {
      method: "POST",
    },
    token,
  );
};

export const updateMemberRole = async (
  orgId: string,
  userId: string,
  role: string,
  token?: string,
): Promise<void> => {
  await apiFetch(
    `/organisations/${orgId}/members/${userId}`,
    {
      method: "PATCH",
      body: JSON.stringify({ role }),
      headers: { "Content-Type": "application/json" },
    },
    token,
  );
};

export const removeMember = async (
  orgId: string,
  userId: string,
  token?: string,
): Promise<void> => {
  await apiFetch(
    `/organisations/${orgId}/members/${userId}`,
    {
      method: "DELETE",
    },
    token,
  );
};

export const checkSlugAvailability = async (
  slug: string,
  token?: string,
): Promise<boolean> => {
  const result = await apiFetch(
    `/organisations/slug-check?slug=${slug}`,
    {},
    token,
  );
  return result.available;
};

// --- Plans ---

export const getPlans = async (
  orgId?: string,
  token?: string,
): Promise<TaskPlan[]> => {
  if (!orgId) return [];
  return apiFetch(`/plans?organisationId=${orgId}`, {}, token);
};

export const savePlan = async (
  plan: TaskPlan,
  token?: string,
): Promise<void> => {
  await apiFetch(
    "/plans",
    {
      method: "POST",
      body: JSON.stringify(plan),
      headers: { "Content-Type": "application/json" },
    },
    token,
  );
};

export const getPlanById = async (
  id: string,
  token?: string,
): Promise<TaskPlan> => {
  return apiFetch(`/plans/${id}`, {}, token);
};

export const getEditorContext = async (
  orgId: string,
  planId?: string,
  token?: string,
): Promise<{ plan: TaskPlan | null; members: User[]; teams: Team[] }> => {
  return apiFetch(
    `/organisations/${orgId}/editor-context${planId ? `/${planId}` : ""}`,
    {},
    token,
  );
};

export const getRunnerContext = async (
  orgId: string,
  runId: string,
  token?: string,
): Promise<{ run: PlanRun; members: User[]; teams: Team[] }> => {
  return apiFetch(`/organisations/${orgId}/runner-context/${runId}`, {}, token);
};

export const deletePlan = async (id: string, token?: string): Promise<void> => {
  await apiFetch(`/plans/${id}`, { method: "DELETE" }, token);
};

// --- Runs ---

export const getRuns = async (
  orgId?: string,
  token?: string,
): Promise<PlanRun[]> => {
  if (!orgId) return [];
  return apiFetch(`/runs?organisationId=${orgId}`, {}, token);
};

export const getRunsForPlan = async (
  planId: string,
  token?: string,
): Promise<PlanRun[]> => {
  return apiFetch(`/plans/${planId}/runs`, {}, token);
};

export const startRun = async (
  planId: string,
  organisationId: string,
  token?: string,
  name?: string,
  scheduledStartTime?: number,
): Promise<PlanRun> => {
  return apiFetch(
    "/runs",
    {
      method: "POST",
      body: JSON.stringify({
        planId,
        organisationId,
        name,
        scheduledStartTime,
      }),
      headers: { "Content-Type": "application/json" },
    },
    token,
  );
};

export const saveRun = async (run: PlanRun, token?: string): Promise<void> => {
  // This is likely mostly replaced by startRun and internal state updates,
  // but might be used for manual updates from the UI if we kept that pattern.
  // For now, no-op or warn.
  console.warn("saveRun is deprecated in favor of API actions");
};

export const getRunById = async (
  id: string,
  token?: string,
): Promise<PlanRun> => {
  return apiFetch(`/runs/${id}`, {}, token);
};

export const deleteRun = async (id: string, token?: string): Promise<void> => {
  await apiFetch(`/runs/${id}`, { method: "DELETE" }, token);
};

export const completeTask = async (
  runId: string,
  taskId: string,
  token?: string,
  notes?: string,
): Promise<void> => {
  await apiFetch(
    `/runs/${runId}/tasks/${taskId}/complete`,
    {
      method: "POST",
      body: JSON.stringify({ notes }),
      headers: { "Content-Type": "application/json" },
    },
    token,
  );
};

export const skipTask = async (
  runId: string,
  taskId: string,
  token?: string,
  notes?: string,
): Promise<void> => {
  await apiFetch(
    `/runs/${runId}/tasks/${taskId}/skip`,
    {
      method: "POST",
      body: JSON.stringify({ notes }),
      headers: { "Content-Type": "application/json" },
    },
    token,
  );
};

export const forceStartRun = async (
  runId: string,
  token?: string,
): Promise<void> => {
  await apiFetch(
    `/runs/${runId}/start`,
    {
      method: "POST",
    },
    token,
  );
};

export const retryTask = async (
  runId: string,
  taskId: string,
  token?: string,
): Promise<void> => {
  await apiFetch(
    `/runs/${runId}/tasks/${taskId}/retry`,
    {
      method: "POST",
    },
    token,
  );
};

export const saveShare = async (
  planId: string,
  granteeType: string,
  granteeId: string,
  permission: string,
  token?: string,
): Promise<{ id: string }> => {
  return apiFetch(
    `/plans/${planId}/shares`,
    {
      method: "POST",
      body: JSON.stringify({ granteeType, granteeId, permission }),
      headers: { "Content-Type": "application/json" },
    },
    token,
  );
};

export const deleteShare = async (
  planId: string,
  granteeType: string,
  granteeId: string,
  token?: string,
): Promise<void> => {
  await apiFetch(
    `/plans/${planId}/shares`,
    {
      method: "DELETE",
      body: JSON.stringify({ granteeType, granteeId }),
      headers: { "Content-Type": "application/json" },
    },
    token,
  );
};

// --- Teams & Users ---

export const getTeams = async (
  orgId?: string,
  token?: string,
): Promise<Team[]> => {
  if (!orgId) return [];
  const teams = await apiFetch(`/teams?organisationId=${orgId}`, {}, token);
  // Backwards compatibility for memberIds
  return teams.map((t: any) => ({
    ...t,
    members: t.members || [],
    memberIds: t.members
      ? t.members.map((m: any) => m.userId)
      : t.memberIds || [],
  }));
};

export const saveTeam = async (team: Team, token?: string): Promise<void> => {
  await apiFetch(
    "/teams",
    {
      method: "POST",
      body: JSON.stringify(team),
      headers: { "Content-Type": "application/json" },
    },
    token,
  );
};

export const deleteTeam = async (id: string, token?: string): Promise<void> => {
  await apiFetch(`/teams/${id}`, { method: "DELETE" }, token);
};

export const getUsers = async (
  orgId?: string,
  token?: string,
): Promise<User[]> => {
  return apiFetch(`/organisations/${orgId}/members`, {}, token);
};

// --- Invitations ---

export const inviteUser = async (
  orgId: string,
  email: string,
  role: string = "MEMBER",
  token?: string,
): Promise<{ success: boolean; id: string; token: string }> => {
  return apiFetch(
    `/organisations/${orgId}/invitations`,
    {
      method: "POST",
      body: JSON.stringify({ email, role }),
      headers: { "Content-Type": "application/json" },
    },
    token,
  );
};

export const getOrgInvitations = async (
  orgId: string,
  token?: string,
): Promise<Invitation[]> => {
  return apiFetch(`/organisations/${orgId}/invitations`, {}, token);
};

export const getInvitations = async (token?: string): Promise<Invitation[]> => {
  return apiFetch("/invitations", {}, token);
};

export const acceptInvitation = async (
  id: string,
  token?: string,
): Promise<void> => {
  await apiFetch(`/invitations/${id}/accept`, { method: "POST" }, token);
};

export const declineInvitation = async (
  id: string,
  token?: string,
): Promise<void> => {
  await apiFetch(`/invitations/${id}/decline`, { method: "POST" }, token);
};

export const cancelInvitation = async (
  id: string,
  token?: string,
): Promise<void> => {
  await apiFetch(`/invitations/${id}`, { method: "DELETE" }, token);
};

export const deleteUserAccount = async (token?: string): Promise<void> => {
  await apiFetch("/users/me", { method: "DELETE" }, token);
};

export const getUserPreferences = async (
  token?: string,
): Promise<{
  emailInvitations: boolean;
  pushInvitations: boolean;
  emailTaskAssignments: boolean;
  pushTaskAssignments: boolean;
}> => {
  return apiFetch("/users/preferences", {}, token);
};

export const checkTrialEligibility = async (
  token?: string,
): Promise<{ eligible: boolean }> => {
  return apiFetch("/users/trial-eligibility", {}, token);
};

export const updateUserPreferences = async (
  preferences: {
    emailInvitations: boolean;
    pushInvitations: boolean;
    emailTaskAssignments: boolean;
    pushTaskAssignments: boolean;
  },
  token?: string,
): Promise<void> => {
  await apiFetch(
    "/users/preferences",
    {
      method: "PATCH",
      body: JSON.stringify(preferences),
      headers: { "Content-Type": "application/json" },
    },
    token,
  );
};

export const updateRunPresence = async (
  runId: string,
  userInfo: { name: string; initials: string; imageUrl?: string },
  token?: string,
): Promise<void> => {
  await apiFetch(
    `/runs/${runId}/presence`,
    {
      method: "POST",
      body: JSON.stringify(userInfo),
      headers: { "Content-Type": "application/json" },
    },
    token,
  );
};

export const getRunPresence = async (
  runId: string,
  token?: string,
): Promise<
  {
    userId: string;
    name: string;
    initials: string;
    imageUrl?: string;
    lastSeenAt: number;
  }[]
> => {
  return apiFetch(`/runs/${runId}/presence`, {}, token);
};

export const getActivityLogs = async (
  orgId: string,
  token?: string,
  params: {
    limit?: number;
    offset?: number;
    action?: string;
  } = {},
): Promise<{ logs: any[]; total: number }> => {
  const query = new URLSearchParams();
  query.append("organisationId", orgId);
  if (params.limit) query.append("limit", params.limit.toString());
  if (params.offset) query.append("offset", params.offset.toString());
  if (params.action) query.append("action", params.action);

  return apiFetch(`/activity?${query.toString()}`, {}, token);
};

// --- Billing ---

export const createCheckoutSession = async (
  orgId: string,
  planId: string,
  token?: string,
): Promise<{ url: string }> => {
  return apiFetch(
    "/billing/create-checkout-session",
    {
      method: "POST",
      body: JSON.stringify({ orgId, planId }),
      headers: { "Content-Type": "application/json" },
    },
    token,
  );
};

export const createPortalSession = async (
  orgId: string,
  token?: string,
): Promise<{ url: string }> => {
  return apiFetch(
    "/billing/create-portal-session",
    {
      method: "POST",
      body: JSON.stringify({ orgId }),
      headers: { "Content-Type": "application/json" },
    },
    token,
  );
};

export const getBillingUsage = async (
  orgId: string,
  token?: string,
): Promise<any> => {
  return apiFetch(`/billing/usage/${orgId}`, {}, token);
};
